import { PropsWithChildren } from "react"
import { makeStyles } from "tss-react/mui"
import { MenuItem, Stack, Tooltip, Typography } from "@mui/material"
import { MAX_INPUT_REFILL_LENGTH } from "../../../../../modules/nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import { DynamicConfigField } from "./formTypes"
import { SelectedMainUserState } from "../../createOrderTypes"
import { DynamicAutocomplete } from "./DynamicAutocomplete"
import { isRenderableConfigField } from "./helpers"
import { SwitchFieldRow } from "./SwitchFieldRow"
import {
    FormikCheckbox,
    FormikDateTimePicker,
    FormikDropdown,
    FormikNumberField,
    FormikSwitch,
    FormikTextField,
} from "../../../forms/Formik"

const useStyles = makeStyles()((theme) => ({
    messageInfo: {
        borderRadius: ".25rem",
        padding: ".5rem .75rem",
        color: theme.palette.tailwind.blue[950],
        background: theme.palette.tailwind.sky[100],
    },
}))

type DynamicFormFieldGroupWrapProps = Readonly<PropsWithChildren<DynamicFormFieldProps>>

export const DynamicFormFieldGroupWrap = ({ config, children }: DynamicFormFieldGroupWrapProps) => {
    return (
        <Tooltip title={config?.description && config?.label ? config.description : ""} arrow>
            <Stack gap=".5rem">{children}</Stack>
        </Tooltip>
    )
}
type DynamicFormFieldProps = {
    config: DynamicConfigField
    user?: SelectedMainUserState
    updateTextfieldClassName?: string
    disabled?: boolean
}

export function DynamicFormField({ config, user, updateTextfieldClassName, disabled }: DynamicFormFieldProps) {
    const { classes } = useStyles()

    const { key, inputField, label, description } = config

    if (!inputField || !isRenderableConfigField(config)) return null

    const { type, isRequired, maxLength, maxValue, isMultiline, data, predefined } = inputField

    const mainLabel = (label || description || "") + (isRequired ? " *" : "")

    switch (type) {
        case "textField":
            return (
                <DynamicFormFieldGroupWrap config={config}>
                    <FormikTextField
                        name={key as string}
                        label={label || description}
                        multiline={isMultiline}
                        fullWidth
                        inputProps={{ maxLength }}
                        InputLabelProps={{ required: isRequired }}
                        className={updateTextfieldClassName}
                        size="small"
                    />
                </DynamicFormFieldGroupWrap>
            )
        case "number":
            return (
                <DynamicFormFieldGroupWrap config={config}>
                    <FormikNumberField
                        name={key as string}
                        label={label || description}
                        fullWidth
                        integerOnly
                        maxLength={maxLength || MAX_INPUT_REFILL_LENGTH}
                        max={maxValue}
                        InputLabelProps={{ required: isRequired }}
                        className={updateTextfieldClassName}
                        size="small"
                        disabled={disabled}
                    />
                </DynamicFormFieldGroupWrap>
            )

        case "checkbox":
            return <FormikCheckbox name={key as string}>{description}</FormikCheckbox>

        case "select":
        case "singleSelect":
            const options = config.selectValues?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))

            const SelectContent = (
                <FormikDropdown fullWidth name={key as string} label={mainLabel} size="small">
                    {options}
                </FormikDropdown>
            )

            return type === "select" ? (
                SelectContent
            ) : (
                <DynamicFormFieldGroupWrap config={config}>{SelectContent}</DynamicFormFieldGroupWrap>
            )

        case "dateTimePicker":
        case "dateTime":
            return (
                <DynamicFormFieldGroupWrap config={config}>
                    <FormikDateTimePicker
                        name={key as string}
                        range="all"
                        label={mainLabel}
                        format={data}
                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                    />
                </DynamicFormFieldGroupWrap>
            )

        case "label":
            return <Typography>{predefined?.text || description}</Typography>

        case "component":
            return (
                <Typography variant="body2" color="textSecondary">
                    {description}
                </Typography>
            )

        case "toogle":
            return (
                <SwitchFieldRow label={label} description={description}>
                    <FormikSwitch name={key as string} disabled={disabled} />
                </SwitchFieldRow>
            )
        case "autocomplete":
            const details = config?.description && config?.label ? config.description : ""
            return (
                <Stack gap="1rem">
                    {!!details && (
                        <Typography variant="body2" className={classes.messageInfo}>
                            {details}
                        </Typography>
                    )}
                    <DynamicAutocomplete config={config} user={user} />
                </Stack>
            )

        default:
            return null
    }
}
